import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const EditReview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const initialFormState = {
    title_ar: "",
    title_en: "",
    image: null,
    old_image_url: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchReview = async () => {
      try {
        const { data } = await api.get(`/reviews/${id}`);
        const review = data.data;
        setFormData({
          ...initialFormState,
          title_ar: review.title_ar ?? "",
          title_en: review.title_en ?? "",
          old_image_url: review.image_url ?? "",
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: t("alerts.error"),
          text: t("alerts.failed_fetch"),
          confirmButtonColor: "#3085d6",
        });
      }
    };
    fetchReview();
  }, [id, t]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title_ar.trim()) newErrors.title_ar = t("errors.required");
    if (!formData.title_en.trim()) newErrors.title_en = t("errors.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      icon: "error",
      title: t("alerts.error"),
      text: message,
      confirmButtonColor: "#3085d6",
    });
  };

  const showSuccessAlert = () => {
    Swal.fire({
      icon: "success",
      title: t("alerts.success"),
      text: t("alerts.review_updated"),
      confirmButtonColor: "#3085d6",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showErrorAlert(t("alerts.fix_errors"));
      return;
    }

    setIsLoading(true);
    Swal.fire({
      title: t("alerts.processing"),
      html: t("alerts.please_wait"),
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    const data = new FormData();
    for (const key in formData) {
      if (
        key !== "old_image_url" &&
        formData[key] !== null &&
        formData[key] !== ""
      ) {
        data.append(key, formData[key]);
      }
    }

    try {
      await api.post(`/reviews/${id}?_method=PUT`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.close();
      showSuccessAlert();
      navigate("/reviews");
    } catch (err) {
      Swal.close();
      const errorMessage =
        err.response?.data?.message || err.message || t("alerts.failed_update");
      showErrorAlert(errorMessage);
      if (err.response?.data?.errors) setErrors(err.response.data.errors);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container my-5 py-5">
      <div className="add-product-form-container">
        <h2 className="mb-4">{t("edit_review.title")}</h2>
        <form onSubmit={handleSubmit} noValidate>
          <div className="row">
            {/* Arabic Title */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t("edit_review.title_ar")} *
              </label>
              <input
                type="text"
                className={`form-control ${
                  errors.title_ar ? "is-invalid" : ""
                }`}
                name="title_ar"
                value={formData.title_ar}
                onChange={handleChange}
              />
              {errors.title_ar && (
                <div className="invalid-feedback">{errors.title_ar}</div>
              )}
            </div>

            {/* English Title */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t("edit_review.title_en")} *
              </label>
              <input
                type="text"
                className={`form-control ${
                  errors.title_en ? "is-invalid" : ""
                }`}
                name="title_en"
                value={formData.title_en}
                onChange={handleChange}
              />
              {errors.title_en && (
                <div className="invalid-feedback">{errors.title_en}</div>
              )}
            </div>

            {/* Image Upload */}
            <div className="col-12 mb-3">
              <label className="form-label">{t("edit_review.image")}</label>
              <input
                type="file"
                className="form-control"
                name="image"
                onChange={handleChange}
                accept="image/*"
              />
            </div>

            {/* Image Preview */}
            {(formData.image || formData.old_image_url) && (
              <div className="col-12 mb-3 text-center">
                <img
                  src={
                    formData.image
                      ? URL.createObjectURL(formData.image)
                      : formData.old_image_url
                  }
                  alt="Preview"
                  className="img-thumbnail"
                  style={{ maxHeight: "200px" }}
                />
              </div>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                {t("edit_review.updating")}
              </>
            ) : (
              t("edit_review.update")
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditReview;
